import React, { FunctionComponent, PropsWithChildren, useEffect, useState, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Color } from '../../config/Color';
import { ANIMATION_CONSTANTS } from '../flows/shared/AnimationConstants';

// Animation configuration for AI-like typewriter effect
const TEXT_ANIMATION_CONFIG = {
  minDelay: 8, // Minimum delay between characters (ms) - much faster
  maxDelay: 25, // Maximum delay between characters (ms) - much faster
  thinkingPauseChance: 0.05, // 5% chance to pause and "think" (reduced from 15%)
  thinkingPauseMinDuration: 150, // Minimum thinking pause (ms) - shorter
  thinkingPauseMaxDuration: 400, // Maximum thinking pause (ms) - shorter
  // Characters that trigger thinking pauses (only major punctuation)
  thinkingTriggers: ['.', '!', '?'],
};

// Generic animated text wrapper component
type AnimatedTextWrapperProps = PropsWithChildren<{
  animationEnabled?: boolean;
  baseDelay?: number;
  onComplete?: () => void;
}>;

const AnimatedTextWrapper: FunctionComponent<AnimatedTextWrapperProps> = ({
  children,
  animationEnabled = false,
  baseDelay = 0,
  onComplete,
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const fullText = typeof children === 'string' ? children : '';

  useEffect(
    function handleTypewriterAnimation() {
      if (!animationEnabled || typeof children !== 'string') {
        setDisplayedText(fullText);
        onComplete?.();
        return;
      }

      // Reset state when text changes
      setDisplayedText('');

      let currentIndex = 0;
      let timeoutId: NodeJS.Timeout;
      let animationActive = true;

      const typeNextChar = () => {
        if (!animationActive) return;

        if (currentIndex < fullText.length) {
          const currentChar = fullText[currentIndex];
          setDisplayedText(fullText.slice(0, currentIndex + 1));
          currentIndex++;

          // Base random delay for next character
          let delay =
            TEXT_ANIMATION_CONFIG.minDelay +
            Math.random() * (TEXT_ANIMATION_CONFIG.maxDelay - TEXT_ANIMATION_CONFIG.minDelay);

          // Check if we should add a thinking pause
          const shouldPause =
            Math.random() < TEXT_ANIMATION_CONFIG.thinkingPauseChance ||
            TEXT_ANIMATION_CONFIG.thinkingTriggers.includes(currentChar);

          if (shouldPause) {
            // Add a random thinking pause
            const thinkingDelay =
              TEXT_ANIMATION_CONFIG.thinkingPauseMinDuration +
              Math.random() *
                (TEXT_ANIMATION_CONFIG.thinkingPauseMaxDuration -
                  TEXT_ANIMATION_CONFIG.thinkingPauseMinDuration);

            delay += thinkingDelay;
          }

          timeoutId = setTimeout(typeNextChar, delay);
        } else {
          // Animation complete
          onComplete?.();
        }
      };

      // Start typing after base delay
      const startTimeout = setTimeout(() => {
        if (animationActive) {
          typeNextChar();
        }
      }, baseDelay);

      return () => {
        animationActive = false;
        clearTimeout(startTimeout);
        clearTimeout(timeoutId);
      };
    },
    [fullText, animationEnabled, baseDelay, onComplete]
  );

  if (!animationEnabled || typeof children !== 'string') {
    return <>{children}</>;
  }

  return <>{displayedText}</>;
};

// Helper to create a text component with optional animation
type TextComponentProps = PropsWithChildren<{
  animationEnabled?: boolean;
  animationIndex?: number;
  onAnimationComplete?: () => void;
}>;

// Create text component with sequential animation
const createTextComponent = <P extends Record<string, unknown>>(
  StyledComponent: React.ComponentType<P>
): FunctionComponent<P & TextComponentProps> => {
  const Component: FunctionComponent<P & TextComponentProps> = ({
    children,
    animationEnabled = false,
    animationIndex = 0,
    onAnimationComplete,
    ...props
  }) => {
    // Memoize expensive delay calculation
    const baseDelay = useMemo(() => {
      const avgCharDelay = (TEXT_ANIMATION_CONFIG.minDelay + TEXT_ANIMATION_CONFIG.maxDelay) / 2;
      return animationIndex * (ANIMATION_CONSTANTS.ESTIMATED_CHARS_PER_BLOCK * avgCharDelay);
    }, [animationIndex]);

    return (
      <StyledComponent {...(props as P)}>
        <AnimatedTextWrapper
          animationEnabled={animationEnabled}
          baseDelay={baseDelay}
          onComplete={onAnimationComplete}
        >
          {children}
        </AnimatedTextWrapper>
      </StyledComponent>
    );
  };
  Component.displayName = `Animated${StyledComponent.displayName ?? 'Text'}`;
  return Component;
};

/* Use this style rather than e.g. `font-family: 'Circular Regular' to standardise the spacing,
 * which is reduced from the font's defaults. */
export const bodyFontStyle = css`
  font-family: ${({ theme }) => theme.font.primary};
  /* Circular sets the following as tabular-num for some reason causing extra character spacing so we need
	* reset them back to normal. */
  font-variant: normal;
  font-feature-settings: normal;
  color: ${Color.content.default};
`;

// DEPRECATION WARNING: This component is deprecated and will be removed
export const Heading = styled.h1`
  font-family: ${({ theme }) => theme.font.heading};
  font-size: ${({ theme }) => theme.fontSize.md};
  margin: ${({ theme }) => `${theme.spacing._24px} 0`};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  color: ${Color.content.default};
`;

export const Heading1 = styled.h1`
  font-family: ${({ theme }) => theme.font.heading};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: 1.125;
  margin-bottom: 0.5em;
  color: ${Color.content.default};
  white-space: pre-line;
  letter-spacing: 0;
  font-variant: normal;
  font-feature-settings: normal;
`;

const Heading2Base = styled.h2`
  font-family: ${({ theme }) => theme.font.heading};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.xmd};
  color: ${Color.content.default};
  line-height: 1.2;
  margin-bottom: 0.5em;
  white-space: pre-line;
  letter-spacing: 0;
  font-variant: normal;
  font-feature-settings: normal;
`;

export const Heading2 = createTextComponent(Heading2Base);

// Create base styled components
const Heading3Base = styled.h3`
  font-family: ${({ theme }) => theme.font.heading};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: calc(1 + 1 / 3);
  margin-bottom: 0.2em;
  color: ${Color.content.default};
  white-space: pre-line;
  letter-spacing: 0;
  font-variant: normal;
  font-feature-settings: normal;
`;

export const Heading3 = createTextComponent(Heading3Base);

export const Heading4 = styled.h4`
  font-family: ${({ theme }) => theme.font.heading};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: calc(1 + 1 / 3);
  margin-bottom: 0.2em;
  color: ${Color.content.default};
  white-space: pre-line;
  letter-spacing: 0;
  font-variant: normal;
  font-feature-settings: normal;
`;

// Same as BodySM, but semantically different
export const Heading5 = styled.h5`
  ${bodyFontStyle}
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 27px;
  letter-spacing: 0.025em;
  margin-bottom: 0.2em;
  color: ${Color.content.default};
  white-space: pre-line;
`;

type BodyProp = { bold?: boolean };
const BodyBase = styled.p<BodyProp>`
  ${bodyFontStyle}
  font-weight: ${({ bold, theme }) => (bold ? theme.fontWeight.medium : '')};
`;

export const Body = createTextComponent(BodyBase);

/** Hero-style title (22px, medium weight). Use for payment and insurer flow headings. */
export const HeroTitle = styled(Body)`
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: 22px;
  margin-bottom: ${({ theme }) => theme.spacing._16px};
  line-height: 1.3;
`;

const BodySMBase = styled(BodyBase)<BodyProp>`
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 27px;
  letter-spacing: 0.5px;
`;

export const BodySM = createTextComponent(BodySMBase);

export const BodyXS = styled(Body)<BodyProp>`
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const BodyXXSBase = styled(Body)<BodyProp>`
  font-size: ${({ theme }) => theme.fontSize.xxs};
  line-height: 18px;
`;

export const BodyXXS = createTextComponent(BodyXXSBase);

export const BodyXXXS = styled(Body)<BodyProp>`
  font-size: ${({ theme }) => theme.fontSize.xxxs};
  line-height: 13px;
`;

export const Caption = styled(Body)<BodyProp>`
  font-size: ${({ theme }) => theme.fontSize.xxxs};
  line-height: 16px;
`;

export const Footnote = styled(BodyXXS)`
  overflow-wrap: break-word;
  & > sup {
    padding-right: 0.5em;
  }
`;

export const Title = styled.h1`
  font-family: ${({ theme }) => theme.font.heading};
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${Color.content.default};
`;

export const LargeTitle = styled.h1`
  font-family: ${({ theme }) => theme.font.heading};
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${Color.content.default};
  line-height: 80px;
  letter-spacing: -0.03em;
`;

export const Label = styled.label`
  ${bodyFontStyle}
`;

export const Emphasis = styled.span`
  ${bodyFontStyle}
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${Color.content.default};
`;

export const ErrorBody = styled(Body)<BodyProp>`
  color: ${Color.state.error};
`;

// Font config that should be passed to Stripe Elements
// These should be kept in sync with font definitions in theme.less
// (but note that Stripe doesn't handle newlines in the src correctly)
// note: Relative font paths work won't work if running on http (ie local dev); must be https
export const stripeElementsOptionsFonts = [
  {
    family: 'Circular',
    fontVariant: 'normal',
    fontFeatureSettings: 'normal',
    src: `local('Circular Regular'),
		local('Circular-Regular'),
		url(/fonts/circular/Circular-Regular.woff2) format('woff2')`,
    weight: '400',
  },
];

// Stripe components are in an iframe; they must have been defined in stripeElementsOptionsFonts
// to be usable
export const stripeFontFamily = 'Circular, Helvetica Neue, Helvetica, sans-serif';

// Currency text styling - used for displaying currency codes smaller than the price
export const Currency = styled.span`
  font-size: 0.8em;
  margin-left: 2px;
`;
